import React, { useState } from 'react';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Card } from '../../components/ui/Card';
import { mockBranches, mockUsers } from '../../data/mockData';
import { showError } from '../../components/ui/Toast';
interface UserFormProps {
  onCancel: () => void;
  onSubmit: () => void;
}
export function UserForm({
  onCancel,
  onSubmit
}: UserFormProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    cnic: '',
    branchId: '',
    role: '',
    additionalInfo1: '',
    additionalInfo2: '',
    additionalInfo3: '',
    additionalInfo4: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const updateField = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error on change
    if (errors[field]) {
      setErrors(prev => {
        const next = {
          ...prev
        };
        delete next[field];
        return next;
      });
    }
  };
  const markTouched = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    validateField(field, formData[field as keyof typeof formData]);
  };
  const validateField = (field: string, value: string): string | null => {
    switch (field) {
      case 'fullName':
        if (!value.trim()) return 'Full name is required';
        return null;
      case 'email':
        {
          if (!value.trim()) return 'Email is required';
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) return 'Please enter a valid email address';
          const emailExists = mockUsers.some(u => u.email.toLowerCase() === value.toLowerCase());
          if (emailExists) return 'This email is already registered';
          return null;
        }
      case 'mobile':
        {
          if (!value.trim()) return 'Mobile number is required';
          const digitsOnly = value.replace(/\D/g, '');
          if (!digitsOnly.startsWith('03')) return 'Mobile number must start with 03';
          if (digitsOnly.length !== 11) return 'Mobile number must be exactly 11 digits';
          return null;
        }
      case 'cnic':
        {
          if (!value.trim()) return 'CNIC is required';
          const cnicDigits = value.replace(/\D/g, '');
          if (cnicDigits.length !== 13) return 'CNIC must be exactly 13 digits';
          const cnicExists = mockUsers.some(u => u.cnic.replace(/\D/g, '') === cnicDigits);
          if (cnicExists) return 'This CNIC is already registered';
          return null;
        }
      case 'branchId':
        if (!value) return 'Please select a branch';
        return null;
      case 'role':
        if (!value) return 'Please select a role';
        return null;
      default:
        return null;
    }
  };
  const validateAll = (): boolean => {
    const requiredFields = ['fullName', 'email', 'mobile', 'cnic', 'branchId', 'role'];
    const newErrors: Record<string, string> = {};
    const allTouched: Record<string, boolean> = {};
    requiredFields.forEach(field => {
      allTouched[field] = true;
      const error = validateField(field, formData[field as keyof typeof formData]);
      if (error) newErrors[field] = error;
    });
    setTouched(prev => ({
      ...prev,
      ...allTouched
    }));
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Auto-format CNIC as XXXXX-XXXXXXX-X
  const handleCnicChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 13);
    let formatted = digits;
    if (digits.length > 5) {
      formatted = digits.slice(0, 5) + '-' + digits.slice(5);
    }
    if (digits.length > 12) {
      formatted = digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12);
    }
    updateField('cnic', formatted);
  };
  // Auto-format mobile as 03XX-XXXXXXX
  const handleMobileChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = digits.slice(0, 4) + '-' + digits.slice(4);
    }
    updateField('mobile', formatted);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) {
      showError('Validation Error', 'Please fix the errors before submitting.');
      return;
    }
    onSubmit();
  };
  return <div className="space-y-6 max-w-3xl mx-auto" data-id="element-640">
      <div className="flex items-center space-x-4" data-id="element-641">
        <Button variant="ghost" onClick={onCancel} data-id="element-642">
          <ArrowLeft className="w-5 h-5 mr-2" data-id="element-643" />
          Back
        </Button>
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-644">Add New User</h2>
      </div>

      <form onSubmit={handleSubmit} noValidate data-id="element-645">
        {/* Basic Information */}
        <Card className="space-y-6" data-id="element-646">
          <div data-id="element-647">
            <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-648">
              Basic Information
            </h3>
            <p className="text-sm text-gray-500" data-id="element-649">
              Enter the user's personal and contact details
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-650">
            <Input label="User Full Name" placeholder="e.g. Muhammad Ali Khan" required value={formData.fullName} onChange={e => updateField('fullName', e.target.value)} onBlur={() => markTouched('fullName')} error={touched.fullName ? errors.fullName : undefined} data-id="element-651" />
            <Input label="User Email" type="email" placeholder="user@cdns.gov.pk" required value={formData.email} onChange={e => updateField('email', e.target.value)} onBlur={() => markTouched('email')} error={touched.email ? errors.email : undefined} helperText={!touched.email || errors.email ? undefined : 'Email must be unique across the system'} data-id="element-652" />
            <Input label="User Mobile #" placeholder="03XX-XXXXXXX" required value={formData.mobile} onChange={e => handleMobileChange(e.target.value)} onBlur={() => markTouched('mobile')} error={touched.mobile ? errors.mobile : undefined} helperText={!touched.mobile || errors.mobile ? undefined : 'Starts with 03, 11 digits total'} data-id="element-653" />
            <Input label="User CNIC" placeholder="XXXXX-XXXXXXX-X" required value={formData.cnic} onChange={e => handleCnicChange(e.target.value)} onBlur={() => markTouched('cnic')} error={touched.cnic ? errors.cnic : undefined} helperText={!touched.cnic || errors.cnic ? undefined : '13 digit Pakistan National Identity Number'} data-id="element-654" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-655">
            <Select label="Branch" placeholder="Select Branch" options={mockBranches.map(b => ({
            value: b.id,
            label: `${b.branchCode} - ${b.branchName}`
          }))} value={formData.branchId} onChange={e => updateField('branchId', e.target.value)} onBlur={() => markTouched('branchId')} error={touched.branchId ? errors.branchId : undefined} required data-id="element-656" />
            <Select label="Role" placeholder="Select Role" options={[{
            value: 'AdminMaker',
            label: 'Admin Maker'
          }, {
            value: 'AdminChecker',
            label: 'Admin Checker'
          }, {
            value: 'BranchMaker',
            label: 'Branch Maker'
          }, {
            value: 'BranchChecker',
            label: 'Branch Checker'
          }]} value={formData.role} onChange={e => updateField('role', e.target.value)} onBlur={() => markTouched('role')} error={touched.role ? errors.role : undefined} required data-id="element-657" />
          </div>
        </Card>

        {/* Additional Information */}
        <Card className="space-y-6 mt-6" data-id="element-658">
          <div data-id="element-659">
            <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-660">
              Additional Information
            </h3>
            <p className="text-sm text-gray-500" data-id="element-661">
              Optional fields for supplementary user details
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-662">
            <Input label="Additional Info 1" placeholder="Enter additional information" value={formData.additionalInfo1} onChange={e => updateField('additionalInfo1', e.target.value)} data-id="element-663" />
            <Input label="Additional Info 2" placeholder="Enter additional information" value={formData.additionalInfo2} onChange={e => updateField('additionalInfo2', e.target.value)} data-id="element-664" />
            <Input label="Additional Info 3" placeholder="Enter additional information" value={formData.additionalInfo3} onChange={e => updateField('additionalInfo3', e.target.value)} data-id="element-665" />
            <Input label="Additional Info 4" placeholder="Enter additional information" value={formData.additionalInfo4} onChange={e => updateField('additionalInfo4', e.target.value)} data-id="element-666" />
          </div>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6" data-id="element-667">
          <Button type="button" variant="outline" onClick={onCancel} data-id="element-668">
            Cancel
          </Button>
          <Button type="submit" data-id="element-669">Create User</Button>
        </div>
      </form>
    </div>;
}